import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, HardHat } from "lucide-react";
import Seo from "../components/Seo";
import WorldMap from "../components/WorldMap";
import ClientStrip from "../components/ClientStrip";
import CaseStudies from "../components/CaseStudies";
import DivisionsSection from "../components/DivisionsSection";
import IndustryRibbon from "../components/IndustryRibbon";
import PartnerSpotlight from "../components/PartnerSpotlight";
import CountryWatermark from "../components/CountryWatermark";
import ValuesSection from "../components/ValuesSection";
import { Reveal, FadeIn, MaskedLines } from "../components/Reveal";
import { IMAGES, srcSetOf, CONTACT } from "../data/content";
import { useLang } from "../i18n";

const Hero = () => {
  const { t } = useLang();
  const h = t.hero3;
  return (
    <section className="relative isolate overflow-hidden border-b border-[#DDE4EA]" data-testid="home-hero">
      <CountryWatermark codes={["se"]} />
      <img
        src={IMAGES.hero}
        srcSet={srcSetOf("hero")}
        sizes="100vw"
        alt=""
        aria-hidden="true"
        decoding="async"
        className="absolute inset-0 z-0 h-full w-full object-cover object-center opacity-[0.08] nv-kenburns"
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#F4F6F8]/70 via-[#F4F6F8]/40 to-[#F4F6F8]" />
      <div className="relative z-10 mx-auto max-w-[1760px] px-5 pb-16 pt-28 sm:px-8 sm:pb-24 sm:pt-36 lg:pb-32 lg:pt-48">
        <FadeIn>
          <p className="nv-pill font-mono2 text-[10px] uppercase tracking-[0.28em] sm:text-xs" data-testid="hero-kicker">
            <span className="nv-dot" aria-hidden="true" />
            {h.kicker}
          </p>
        </FadeIn>
        <h1 className="font-display mt-6 max-w-4xl text-4xl font-extrabold uppercase leading-[1.02] tracking-tight nv-head-xl sm:text-5xl lg:text-7xl" data-testid="hero-brand">
          <MaskedLines lines={h.heroLines} />
        </h1>
        <FadeIn delay={0.4}>
          <p className="font-display mt-5 max-w-3xl text-lg font-bold leading-snug tracking-tight text-gradient sm:text-xl" data-testid="hero-tagline">{h.tagline}</p>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#41535F] md:text-lg" data-testid="hero-supporting-text">{h.supporting}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-testid="hero-ctas">
            <Link to="/contact?service=Workforce%20Solutions#enquiry" data-testid="hero-cta-workforce" className="nv-cta-glow inline-flex min-h-[52px] items-center justify-center gap-2 bg-gradient-brand px-7 text-sm font-semibold uppercase tracking-wide text-white">
              {t.cta.requestWorkforce} <ArrowRight size={16} />
            </Link>
            <Link to="/contact" data-testid="hero-cta-team" className="inline-flex min-h-[52px] items-center justify-center gap-2 border border-[#0E1B24]/60 px-7 text-sm font-semibold uppercase tracking-wide text-[#0E1B24] transition-colors duration-300 hover:border-[#0F5C8C] hover:text-[#0F5C8C]">
              {t.cta.talkToTeam} <ArrowRight size={16} />
            </Link>
            <Link to="/for-candidates#apply" data-testid="hero-cta-cv" className="inline-flex min-h-[52px] items-center justify-center gap-2 px-2 text-sm font-semibold uppercase tracking-wide text-[#0F5C8C] transition-colors duration-300 hover:text-[#0A3E63] sm:px-4">
              {t.cta.submitCv} <ArrowRight size={16} />
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLang();
  const h = t.hero3;
  const cards = [
    { id: "hse", to: "/hse", img: "industrial", Icon: ShieldCheck, title: h.hse.title, text: h.hseCardText, cta: h.exploreHse },
    { id: "manpower", to: "/services", img: "construction", Icon: HardHat, title: h.manpower.title, text: h.manpowerCardText, cta: h.exploreManpower },
  ];
  return (
    <>
      <Seo title={t.home.seoTitle} description={t.home.seoDesc} />
      <Hero />

      <DivisionsSection index="02" />

      <ClientStrip index="03" />

      <section className="mx-auto max-w-[1760px] px-5 py-20 sm:px-8 sm:py-28" data-testid="home-services">
        <FadeIn>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#0F5C8C] sm:text-xs">04 // {h.servicesKicker}</p>
        </FadeIn>
        <h2 className="font-display mt-4 max-w-2xl text-2xl font-extrabold tracking-tight nv-head sm:text-3xl">{h.servicesTitle}</h2>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {cards.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.1}>
              <Link to={c.to} data-testid={`home-service-card-${c.id}`} className="group relative block aspect-[16/12] overflow-hidden border border-[#DDE4EA] sm:aspect-[16/10]">
                <img src={IMAGES[c.img]} srcSet={srcSetOf(c.img)} sizes="(max-width:1024px) 100vw, 640px" alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#E8EDF1] via-[#E8EDF1]/75 to-[#E8EDF1]/25" />
                <div className="relative flex h-full flex-col justify-end p-7 sm:p-9">
                  <c.Icon size={30} className="text-[#0F5C8C]" />
                  <h3 className="font-display mt-4 text-2xl font-extrabold tracking-tight nv-head sm:text-3xl">{c.title}</h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-[#41535F]">{c.text}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0F5C8C]">
                    {c.cta} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <CaseStudies index="05" />
      <IndustryRibbon index="06" />
      <ValuesSection index="07" />
      <PartnerSpotlight index="08" />
      <WorldMap index="09" id="where-we-operate" />
    </>
  );
}
